import { cn, cva, NoddiIcon, NoddiIconName, type VariantProps } from 'noddi-ui-common';
import { NoddiIconButton } from '../Inputs/NoddiIconButton';

const containerVariants = cva('rounded-lg border', {
  variants: {
    variant: {
      info: 'border-blue-200 bg-blue-50',
      warning: 'border-yellow-200 bg-yellow-50',
      success: 'border-green-200 bg-green-50',
      error: 'border-red-200 bg-red-50',
      // Neither good nor bad news, e.g. how pricing works. Tinted per brand theme, like a selected card.
      brand: 'border-transparent bg-card-light-background'
    },
    size: {
      default: 'p-4',
      small: 'p-2'
    }
  },
  defaultVariants: {
    variant: 'info',
    size: 'default'
  }
});

const iconVariants = cva('mt-0.5 shrink-0', {
  variants: {
    variant: {
      info: 'text-blue-600',
      warning: 'text-yellow-600',
      success: 'text-green-600',
      error: 'text-red-600',
      brand: 'text-primary'
    }
  },
  defaultVariants: { variant: 'info' }
});

const titleVariants = cva('font-medium', {
  variants: {
    variant: {
      info: 'text-blue-900',
      warning: 'text-yellow-900',
      success: 'text-green-900',
      error: 'text-red-900',
      brand: 'text-primary'
    },
    size: {
      default: 'text-sm',
      small: 'text-xs'
    }
  },
  defaultVariants: { variant: 'info', size: 'default' }
});

const contentVariants = cva('', {
  variants: {
    variant: {
      info: 'text-blue-800',
      warning: 'text-yellow-800',
      success: 'text-green-800',
      error: 'text-red-800',
      brand: 'text-primary'
    },
    size: {
      default: 'text-sm',
      small: 'text-xs'
    }
  },
  defaultVariants: { variant: 'info', size: 'default' }
});

const ICON_SIZE_BY_BANNER_SIZE = { default: 'small', small: 'xSmall' } as const;

const DEFAULT_ICONS: Record<NonNullable<BannerVariant>, NoddiIconName> = {
  info: 'Info',
  warning: 'TriangleAlert',
  success: 'CircleCheck',
  error: 'CircleX',
  brand: 'BadgePercent'
};

type BannerVariant = NonNullable<VariantProps<typeof containerVariants>['variant']>;
type BannerSize = NonNullable<VariantProps<typeof containerVariants>['size']>;

type TextItem = {
  key?: string;
  value: string;
};

interface Props extends VariantProps<typeof containerVariants> {
  title?: string;
  icon?: NoddiIconName;
  texts?: TextItem[];
  /** Sits under the message. It is not added to the bullet list. */
  footer?: React.ReactNode;
  onClose?: () => void;
  className?: string;
  actionNode?: React.ReactNode;
}

export const NoddiInfoBanner = ({
  variant,
  title,
  icon,
  texts,
  footer,
  onClose,
  className,
  size,
  actionNode
}: Props) => {
  const resolvedVariant: BannerVariant = variant ?? 'info';
  const resolvedSize: BannerSize = size ?? 'default';
  const iconName = icon ?? DEFAULT_ICONS[resolvedVariant];
  const hasTexts = !!texts && texts.length > 0;
  const showBullets = !!texts && texts.length > 1;

  return (
    <div className={cn(containerVariants({ variant, size }), className)}>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex flex-1 gap-3'>
          <NoddiIcon
            name={iconName}
            size={ICON_SIZE_BY_BANNER_SIZE[resolvedSize]}
            className={iconVariants({ variant })}
          />
          <div className={cn(contentVariants({ variant, size }), 'flex flex-1 flex-col gap-3')}>
            <div>
              {title && <p className={cn(titleVariants({ variant, size }), hasTexts && 'mb-1')}>{title}</p>}
              {hasTexts && (
                // eslint-disable-next-line noddi/enforce-spacing-scale -- not migrated yet: legacy space-y-1 stack, equals flex flex-col gap-1
                <ul className='space-y-1'>
                  {texts.map((item, index) => (
                    <li key={index}>
                      {item.key ? (
                        <>
                          {showBullets && '• '}
                          <strong>{item.key}:</strong> {item.value}
                        </>
                      ) : (
                        <>
                          {showBullets && '• '}
                          {item.value}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {footer}
            </div>
            {actionNode && <div className='flex flex-wrap gap-2'>{actionNode}</div>}
          </div>
        </div>
        {onClose && <NoddiIconButton onClick={onClose} iconName='X' variant='ghost' size='small' />}
      </div>
    </div>
  );
};
